import React, { useEffect, createContext, useReducer } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col } from 'react-bootstrap';

import axios from 'axios';

import Alert from '../../../components/Alert';

import {
  getReportsServicesTable,
} from '../../../services/endpoints/reports';
import {
  setPageFiltersType,
  setPageFiltersQuery,
  setPageLoader,
  getReportsServicesConfigAction,
  setPageData, setPageClearData,
} from '../../../redux/actions/reports';
import { reportsSelector } from '../../../redux/selectors/reports';
import { checkInterval } from '../helpers';
import ServicesTable from './components/Table';
import { servicesReportsReducer, servicesReportsState } from './reducer';
import {
  REPORTS_SERVICES_DATA_ERROR,
  REPORTS_SERVICES_DATA_SUCCESS,
  REPORTS_SERVICES_INIT,
  REPORTS_SERVICES_LOADING,
} from './reducer/actionTypes';
import { delay } from '../../../utils/helper';
import { servicesCategories, servicesProviders } from '../../../services/endpoints/services';
import { createDataListAfterFilters, createReportList } from './helpers';
import { DEFAULT_FILTERS } from './constants';

let queueTimeout = null;
let cancelToken = axios.CancelToken.source();
let cancelTokenConfig = axios.CancelToken.source();

export const ServicesReportsContext = createContext({});

const ReportsServices = () => {
  const dispatch = useDispatch();

  const [componentStore, componentDispatch] = useReducer(servicesReportsReducer, servicesReportsState);
  const { init, error } = componentStore;

  const reports = useSelector((state) => reportsSelector(state));
  const { isLoading, pageFiltersQuery, pageData } = reports;

  const fetchServicesData = async () => {
    // eslint-disable-next-line no-useless-catch
    try {
      if (!pageData.hasOwnProperty('categories') && !pageData.hasOwnProperty('providers')) {
        const [respProviders, respCategories] = await Promise.all([servicesProviders(), servicesCategories()])
        await dispatch(setPageData({
          providers: respProviders?.data,
          categories: respCategories?.data,
        }))
        return {
          providers: respProviders?.data,
          categories: respCategories?.data,
        }
      }
      return {
        providers: pageData?.providers,
        categories: pageData?.categories,
      }
    } catch (error) {
      throw error;
    }
  }

  const fetchList = async (queue, data) => {
    const { hash, life_time, request_interval } = queue;
    try {
      const response = await getReportsServicesTable(hash ? { hash } : pageFiltersQuery, cancelToken.token);

      if (response.success) {
        if (life_time === 0) {
          clearTimeout(queueTimeout);
          componentDispatch({ type: REPORTS_SERVICES_DATA_ERROR, payload: { error: 'Request life time expired' } })
          dispatch(setPageLoader(false));
        }
        if (response?.data?.status === 'error') {
          clearTimeout(queueTimeout);
          componentDispatch({ type: REPORTS_SERVICES_DATA_ERROR, payload: { error: response?.data?.message ?? 'Something went wrong' } })
          dispatch(setPageLoader(false));
        }
        if (response?.data?.status === 'completed') {
          const pageDataReports = createReportList(response.data.data, data.categories);
          dispatch(setPageData({
            reports: pageDataReports,
          }))
          componentDispatch({ type: REPORTS_SERVICES_DATA_SUCCESS,
            payload: {
              data: createDataListAfterFilters(pageDataReports, pageFiltersQuery),
              filters: pageFiltersQuery,
            } })
          dispatch(setPageLoader(false));
        }
        if (response?.data?.status === 'processing') {
          queueTimeout = setTimeout(() => {
            fetchList(queue, data);
          }, checkInterval(request_interval));
        }
      } else {
        componentDispatch({ type: REPORTS_SERVICES_DATA_ERROR, payload: { error: response?.data?.message ?? 'Something went wrong' } })
        hash && dispatch(setPageLoader(false));
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      hash && dispatch(setPageLoader(false));
      componentDispatch({ type: REPORTS_SERVICES_DATA_ERROR, payload: { error: error?.data?.error_message ?? 'Something went wrong' } })
      throw error;
    }
  };

  const getAllData = async () => {
    componentDispatch({ type: REPORTS_SERVICES_DATA_ERROR, payload: { error: '' } })
    dispatch(setPageLoader(true));
    try {
      const payload = {
        range: pageFiltersQuery.range,
        date_from: pageFiltersQuery.date_from,
        date_to: pageFiltersQuery.date_to,
      }
      const [responseConfig, responseData] = await Promise.all([dispatch(getReportsServicesConfigAction(payload, cancelTokenConfig.token)), fetchServicesData()]);
      await delay(150);
      if (!init) {
        dispatch(setPageFiltersType('services-table'));
        componentDispatch({ type: REPORTS_SERVICES_INIT, payload: { init: true } })
      }
      if (responseConfig?.success) {
        if (responseConfig?.data?.queue?.enabled) {
          queueTimeout = setTimeout(() => {
            fetchList(responseConfig?.data?.queue, responseData);
          }, checkInterval(responseConfig?.data?.queue?.request_interval));
        } else {
          await fetchList(null, responseData);
        }
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      console.log(error)
      componentDispatch({ type: REPORTS_SERVICES_DATA_ERROR, payload: { error: error?.data?.error_message ?? 'Something went wrong' } })
      dispatch(setPageLoader(false));
    }
  };

  const cancelQueue = () => {
    clearTimeout(queueTimeout);
    cancelToken.cancel('Cancel request');
    cancelToken = axios.CancelToken.source();
    cancelTokenConfig.cancel('Cancel request');
    cancelTokenConfig = axios.CancelToken.source();
  };

  useEffect(() => {
    document.title = 'Services';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cancelQueue();
    pageFiltersQuery && getAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFiltersQuery?.range, pageFiltersQuery?.date_from, pageFiltersQuery?.date_to]);

  useEffect(() => {
    if (init) {
      componentDispatch({ type: REPORTS_SERVICES_LOADING, payload: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFiltersQuery?.categories, pageFiltersQuery?.providers, pageFiltersQuery?.status, pageFiltersQuery?.query, pageFiltersQuery?.query_type])

  const updateList = async () => {
    if (init) {
      const data = createDataListAfterFilters(pageData.reports, pageFiltersQuery);
      await delay(500)
      componentDispatch({ type: REPORTS_SERVICES_DATA_SUCCESS, payload: { data, filters: pageFiltersQuery } });
    }
  }
  useEffect(() => {
    updateList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentStore.isLoading])

  useEffect(() => {
    cancelQueue();
    dispatch(setPageClearData());
    dispatch(setPageFiltersType(''));
    dispatch(setPageFiltersQuery(DEFAULT_FILTERS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return null;

  return (
    <ServicesReportsContext.Provider
      value={[componentStore, componentDispatch]}
    >
      <Row>
        <Col xs={12}>
          {error ? <Alert text={error} /> : <ServicesTable />}
        </Col>
      </Row>
    </ServicesReportsContext.Provider>
  );
};

export default ReportsServices;
